'use client'

import { MapSelectedVenueProvider } from '@/contexts/MapSelectedVenue'
import type { PhaseTagType } from '@/frontend-types'
import { getPageUrl } from '@/helpers/breadcrumbs'
import { populated } from '@/helpers/populated'
import { transformImage, transformImages, transformVenueStatus } from '@/helpers/transforms'
import type { Page } from '@/payload-types'
import type { IconName, IconPrefix } from '@fortawesome/fontawesome-svg-core'
import { motion } from 'motion/react'
import { useSearchParams } from 'next/navigation'
import type { FC } from 'react'
import { useMemo, useState } from 'react'
import { ButtonToggle } from '../atoms/ButtonToggle'
import type { CardVenueType } from '../atoms/CardVenue'
import { CardVenue } from '../atoms/CardVenue'
import { FontAwesomeIcon } from '../atoms/FontAwesomeIcon'
import { Grid } from '../atoms/Grid'
import { Toggle } from '../atoms/Toggle'
import { VenueFilters } from '../atoms/VenueFilters'
import ContentBlockWrapper from '../molecules/ContentBlockWrapper'
import { VenuesMap } from '../molecules/VenuesMap/index'
import type { IMarkerData } from '../molecules/VenuesMap/types'

const VENUES_TO_SHOW = 18

export type VenueListBlockProps = {
	venues: Page[]
}

export const VenueListBlock: FC<VenueListBlockProps> = ({ venues }) => {
	const searchParams = useSearchParams()
	const statuses = searchParams.getAll('status')
	const anyFilterApplied = statuses.length > 0

	const [view, setView] = useState('list')

	const filteredVenues = useMemo(() => {
		return venues.filter((venue) => {
			if (!venue.venue) return false

			if (statuses.length > 0 && !statuses.includes(venue.venue.status)) {
				return false
			}

			return true
		})
	}, [venues, statuses])

	// Convert venues to marker data
	const markerData = useMemo(
		() =>
			filteredVenues.reduce<IMarkerData[]>((acc, venue) => {
				if (!venue.venue) return acc
				const { photos, geolocation, city, region, mapData } = venue.venue
				const location = `${city} / ${region}`

				const imageData = populated(photos?.[0]) ? transformImage(photos?.[0]) : undefined
				const imageUrl = imageData?.src
				const imageAlt = imageData?.alt

				const cardLabel = mapData?.cardLabel ? mapData.cardLabel : undefined

				acc.push({
					id: venue.id,
					name: venue.title,
					lat: geolocation[1],
					lng: geolocation[0],
					location: location,
					imageUrl: imageUrl,
					imageAlt: imageAlt,
					iconPrefix: mapData?.faPrefix as IconPrefix,
					iconName: mapData?.faIcon as IconName,
					cardLabel: cardLabel,
					url: getPageUrl(venue),
				})
				return acc
			}, []),
		[filteredVenues],
	)

	// Filter and convert venues to cards
	const cards = useMemo(() => {
		const cards: CardVenueType[] = []

		for (const venue of filteredVenues) {
			if (!venue.venue) continue

			// @TODO apply other filters

			// Convert to card and push to array
			const { status, city, region, photos, phase } = venue.venue
			const location = `${city} / ${region}`
			const images = transformImages(photos)

			const phaseTag: PhaseTagType = {
				phase: phase,
				icon: ['fas', 'timeline'],
			}

			cards.push({
				slug: venue.slug,
				title: venue.title,
				category: transformVenueStatus(status),
				location: location,
				images: images,
				phaseTag: phaseTag,
				url: getPageUrl(venue),
			})
		}
		return cards
	}, [filteredVenues])

	const [showMore, setShowMore] = useState(false)
	const toggleShowMore = () => setShowMore((prev) => !prev)

	const [showFilters, setShowFilters] = useState(searchParams.size > 0)
	const filtersHeight = showFilters ? 'auto' : '0'
	const toggleFilters = () => setShowFilters((prev) => !prev)

	const canShowMore = cards.length > VENUES_TO_SHOW
	const listViewCards = useMemo(() => {
		return showMore ? cards : cards.slice(0, VENUES_TO_SHOW)
	}, [showMore, cards])

	return (
		<MapSelectedVenueProvider>
			<ContentBlockWrapper blockTypes={['standalone']}>
				<div className="col-span-full">
					{/* Title and controls */}
					<div className="mb-5xl flex flex-wrap items-center justify-between gap-xl">
						<h2 id="venues" className="max-tablet-p:w-full text-h2 text-blue">
							{anyFilterApplied ? 'Venues' : 'All venues'} ({cards.length})
						</h2>
						<div className="flex items-center gap-x-lg">
							<ButtonToggle
								active={showFilters}
								label={showFilters ? 'Hide Filters' : 'Show Filters'}
								onClick={toggleFilters}
							/>
							<div className="hidden tablet-l:block">
								<Toggle
									active={view === 'list'}
									labelOne="Grid"
									labelOneId="toggle-grid"
									buttonOneClick={() => setView('list')}
									labelTwo="Map"
									labelTwoId="toggle-map"
									buttonTwoClick={() => setView('map')}
								/>
							</div>
						</div>
					</div>
					{/* Filters */}
					<motion.div
						initial={{ height: filtersHeight }}
						animate={{ height: filtersHeight }}
						className="overflow-hidden"
					>
						<div className="pb-5xl">
							<VenueFilters />
						</div>
					</motion.div>
					<div id="toggle-grid-content" aria-labelledby="toggle-grid" aria-hidden={view !== 'list'}>
						{view === 'list' && (
							<>
								{/* List of venues */}
								<Grid className="gap-y-2xl">
									{listViewCards.map((card) => (
										<div key={card.slug} className="col-span-full tablet-p:col-span-4">
											<CardVenue cardVenue={card} />
										</div>
									))}
								</Grid>
								{/* Show more button (list view only) */}
								{canShowMore && (
									<div className="flex justify-center">
										<button
											className="mt-2xl flex w-fit cursor-pointer items-center gap-x-sm text-button text-blue"
											onClick={toggleShowMore}
										>
											<span>{showMore ? 'Show Less' : 'Show More'}</span>
											<FontAwesomeIcon icon={['fas', showMore ? 'minus' : 'plus']} size="md" />
										</button>
									</div>
								)}
							</>
						)}
					</div>
					{/* Map view */}
					<div id="toggle-map-content" aria-labelledby="toggle-map" aria-hidden={view !== 'map'}>
						{view === 'map' && (
							<div className="w-full h-[calc(100svh-120px)]">
								<VenuesMap markerData={markerData} />
							</div>
						)}
					</div>
				</div>
			</ContentBlockWrapper>
		</MapSelectedVenueProvider>
	)
}

export default VenueListBlock
